import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSites, SitePicker } from "@/components/wp/SitePicker";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, ExternalLink, Layers, Loader2, Paintbrush, RefreshCw, Zap } from "lucide-react";

const TYPE_LABEL = {
  breakdance_template: "Template", breakdance_header: "Header",
  breakdance_footer: "Footer", breakdance_block: "Global block",
  breakdance_popup: "Popup", breakdance_form: "Form",
};

export default function BreakdancePage() {
  const { sites, site, siteId, pick } = useSites();
  const [templates, setTemplates] = useState(null);
  const [tplErr, setTplErr] = useState("");
  const [busy, setBusy] = useState("");

  const d = site?.detected || {};
  const bd = d.breakdance;
  const hasConnector = !!(d.connector || d.connector_api);

  const loadTemplates = useCallback(async () => {
    if (!siteId) return;
    setTemplates(null); setTplErr("");
    try {
      const { data } = await api.get(`/wp/sites/${siteId}/breakdance/templates`);
      setTemplates(data);
    } catch (e) {
      setTplErr(e.response?.data?.detail || "Could not load templates");
    }
  }, [siteId]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const regenerate = async () => {
    setBusy("regen");
    try {
      const { data } = await api.post(`/wp/sites/${siteId}/breakdance/regenerate`);
      toast.success(`Cache cleared — ${data.css_files_deleted} CSS files purged. ` +
        "Breakdance regenerates styles on the next page view.");
    } catch (e) { toast.error(e.response?.data?.detail || "Regeneration failed"); }
    finally { setBusy(""); }
  };

  const togglePlugin = async () => {
    setBusy("toggle");
    try {
      await api.put(`/wp/sites/${siteId}/plugins`, {
        plugin: "breakdance/plugin.php",
        status: bd?.active ? "inactive" : "active",
      });
      await api.post(`/wp/sites/${siteId}/check`);
      toast.success(bd?.active ? "Breakdance deactivated" : "Breakdance activated");
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };

  const downloadConnector = async () => {
    const { data } = await api.get(`/wp/sites/${siteId}/connector-download`,
      { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = "iceberg-connector.zip"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="breakdance-page">
      <div className="flex items-center gap-3">
        <Layers className="h-6 w-6 text-[#002FA7]" />
        <div>
          <h1 className="text-2xl font-bold">Breakdance API</h1>
          <p className="text-sm text-zinc-500">
            Manage Breakdance builder sites — templates, cache regeneration and plugin state.
          </p>
        </div>
      </div>

      <SitePicker sites={sites} siteId={siteId} onPick={pick} />

      {site && (
        <>
          <div className="grid md:grid-cols-3 gap-3">
            <Card><CardContent className="p-4">
              <div className="text-xs text-zinc-500 mb-1">Breakdance status</div>
              {bd ? (
                <>
                  <div className="text-xl font-bold" data-testid="bd-version">v{bd.version}</div>
                  <Badge variant="outline" className={bd.active
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-zinc-100 text-zinc-500 border-zinc-200"}>
                    {bd.active ? "Active" : "Installed, inactive"}
                  </Badge>
                </>
              ) : (
                <div className="text-sm text-zinc-500">
                  Not detected — run a health check on the
                  <Link to="/wordpress?tab=sites" className="text-[#002FA7] underline ml-1">Sites tab</Link>
                </div>
              )}
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-xs text-zinc-500 mb-1">Iceberg Connector</div>
              {hasConnector ? (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Installed</Badge>
              ) : (
                <div className="text-sm space-y-2">
                  <div className="text-zinc-500">Required for cache + template features.</div>
                  <Button variant="outline" size="sm" onClick={downloadConnector}
                    data-testid="bd-connector-download">
                    <Download className="h-4 w-4 mr-1" /> Download plugin
                  </Button>
                </div>
              )}
            </CardContent></Card>
            <Card><CardContent className="p-4 space-y-2">
              <div className="text-xs text-zinc-500 mb-1">Actions</div>
              <Button size="sm" className="w-full" disabled={busy === "regen" || !hasConnector}
                onClick={regenerate} data-testid="bd-regenerate-btn">
                {busy === "regen" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Zap className="h-4 w-4 mr-1" />}
                Regenerate CSS cache
              </Button>
              {bd && (
                <Button size="sm" variant="outline" className="w-full" disabled={busy === "toggle"}
                  onClick={togglePlugin} data-testid="bd-toggle-btn">
                  {busy === "toggle" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Paintbrush className="h-4 w-4 mr-1" />}
                  {bd.active ? "Deactivate Breakdance" : "Activate Breakdance"}
                </Button>
              )}
            </CardContent></Card>
          </div>

          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Templates &amp; global elements</h2>
            <Button variant="outline" size="icon" onClick={loadTemplates}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          {tplErr ? (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900"
              data-testid="bd-templates-warning">
              {tplErr}. Install the Iceberg Connector plugin on the client site to list Breakdance
              templates and clear caches remotely.
            </div>
          ) : !templates ? (
            <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
          ) : (
            <Card><CardContent className="p-0">
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-zinc-50 text-left text-xs text-zinc-500">
                  <th className="p-3">Title</th><th className="p-3">Type</th>
                  <th className="p-3">Status</th><th className="p-3">Modified</th>
                  <th className="p-3 text-right">Edit</th>
                </tr></thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={`${t.type}-${t.id}`} className="border-b" data-testid={`bd-template-${t.id}`}>
                      <td className="p-3 font-medium">{t.title || "(no title)"}</td>
                      <td className="p-3"><Badge variant="outline">{TYPE_LABEL[t.type] || t.type}</Badge></td>
                      <td className="p-3">{t.status}</td>
                      <td className="p-3 text-xs text-zinc-500">{(t.modified || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="p-3 text-right">
                        <a href={t.edit_url} target="_blank" rel="noreferrer">
                          <Button variant="ghost" size="sm"><ExternalLink className="h-4 w-4" /></Button>
                        </a>
                      </td>
                    </tr>
                  ))}
                  {!templates.length && (
                    <tr><td colSpan={5} className="p-8 text-center text-zinc-400">
                      No Breakdance templates found on this site.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </CardContent></Card>
          )}
        </>
      )}
    </div>
  );
}
